import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

let pending = false;
let touchStart = null;
let suppressClickUntil = 0;
let activePicker = null;
let keyboardInteraction = false;

function setPaused(root, paused) {
  root.dataset.cartActive = String(paused);
  root.dispatchEvent(new Event(paused ? 'hero:pause' : 'hero:resume'));
}

function restoreFocus(button) {
  if (!keyboardInteraction) {
    button.dataset.heroPointerFocus = 'true';
    button.blur();
    return;
  }
  delete button.dataset.heroPointerFocus;
  const slide = button.closest('.lngvty-hero__slide');
  if (button.isConnected && slide?.getAttribute('aria-hidden') !== 'true') button.focus({ preventScroll: true });
}

function copyCartFonts(dialog) {
  const cart = document.querySelector('.cart-drawer__dialog');
  if (!cart) return;
  dialog.style.setProperty('--hero-cart-font', getComputedStyle(cart).fontFamily);
  const heading = cart.querySelector('.cart-drawer__heading');
  const checkout = cart.querySelector('.cart__checkout-button');
  if (heading) {
    const style = getComputedStyle(heading);
    dialog.style.setProperty('--hero-cart-heading-font', style.fontFamily);
    dialog.style.setProperty('--hero-cart-heading-weight', style.fontWeight);
  }
  if (checkout) dialog.style.setProperty('--hero-cart-button-weight', getComputedStyle(checkout).fontWeight);
}

function openPicker(button) {
  if (activePicker) return;
  const template = Array.from(document.querySelectorAll('[data-hero-picker-template]'))
    .find(item => item.dataset.heroPickerTemplate === button.dataset.heroPicker);
  if (!template) return;
  const dialog = template.content.querySelector('dialog').cloneNode(true);
  const root = button.closest('.lngvty-hero');
  document.body.append(dialog);
  copyCartFonts(dialog);
  activePicker = dialog;
  setPaused(root, true);
  const form = dialog.querySelector('form');
  const submit = dialog.querySelector('[data-picker-submit]');
  const hint = dialog.querySelector('[data-picker-hint]');
  const hasFlavor = dialog.querySelector('h2').textContent.toLowerCase().includes('flavor');
  submit.textContent = hasFlavor ? 'Select a flavor' : 'Select an option';
  hint.textContent = hasFlavor ? 'Select a flavor to continue' : 'Select an option to continue';
  form.addEventListener('change', () => {
    const selected = form.querySelector('input:checked:not(:disabled)');
    submit.disabled = !selected;
    if (selected) {
      submit.textContent = 'Add to cart';
      hint.textContent = selected.dataset.variantTitle + ' · ' + selected.dataset.price;
    }
  });
  dialog.querySelector('[data-picker-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => {
    activePicker = null;
    dialog.remove();
    if (!pending) { setPaused(root, false); restoreFocus(button); }
  }, { once: true });
  form.addEventListener('submit', event => {
    event.preventDefault();
    const selected = form.querySelector('input:checked:not(:disabled)');
    if (!selected || pending) return;
    if (window.Shopify?.designMode) {
      hint.textContent = 'Preview only. Test this selection in the storefront preview.';
      return;
    }
    addProduct(button, { ...selected.dataset, variantId: selected.value }, dialog);
  });
  dialog.showModal();
}

function beginCartUpdate(drawer) {
  const dialog = drawer.querySelector('dialog');
  dialog.querySelectorAll('.lngvty-hero-cart-error').forEach(node => node.remove());
  dialog.setAttribute('aria-busy', 'true');
  const content = dialog.querySelector('.cart-drawer__content');
  if (content) content.inert = true;
  // Keep the original cart visible. Never replace it with a temporary product card.
  drawer.open();
  return dialog;
}

function finishCartUpdate() {
  document.querySelectorAll('.cart-drawer__dialog').forEach(dialog => {
    dialog.removeAttribute('aria-busy');
    const content = dialog.querySelector('.cart-drawer__content');
    if (content) content.inert = false;
  });
}

function showError(message, button) {
  const drawer = document.querySelector('.cart-drawer__dialog[open]');
  if (drawer) {
    const error = document.createElement('p');
    error.className = 'lngvty-hero-cart-error';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    drawer.querySelector('.cart-drawer__header').after(error);
  } else {
    const id = button.closest('.lngvty-hero').id.replace('LngvtyHero-', '');
    const status = Array.from(document.querySelectorAll('[data-hero-cart-status]')).find(node => node.dataset.heroCartStatus === id);
    if (status) { status.hidden = false; status.textContent = message; }
  }
}

async function addProduct(button, item = button.dataset, picker) {
  const quantity = Number(button.dataset.quantity);
  if (pending || !/^\d+$/.test(item.variantId || '') || !Number.isInteger(quantity) || quantity < 1 || quantity > 10) return;
  const root = button.closest('.lngvty-hero');
  if (window.Shopify?.designMode) {
    showError('Preview only. Test this banner in the storefront preview.', button);
    return;
  }
  pending = true;
  setPaused(root, true);
  button.setAttribute('aria-busy', 'true');
  document.querySelectorAll('[data-hero-cart-status]').forEach(node => { node.hidden = true; node.textContent = ''; });
  picker?.close();
  const drawer = document.querySelector('cart-drawer-component');
  let cartDialog;
  if (typeof drawer?.open === 'function') cartDialog = beginCartUpdate(drawer);
  const routesRoot = window.Shopify?.routes?.root || '/';
  const sectionIds = [...new Set(Array.from(document.querySelectorAll('cart-items-component')).map(node => node.dataset.sectionId).filter(Boolean))].slice(0, 5);
  const headerSectionId = drawer?.closest('.shopify-section')?.id.replace('shopify-section-', '');
  if (headerSectionId && !sectionIds.includes(headerSectionId)) sectionIds.unshift(headerSectionId);
  const sectionUrl = new URL(window.location.href);
  // Keep draft section IDs tied to their preview theme during cart rendering.
  if (window.Shopify?.theme?.role === 'unpublished' || window.Shopify?.theme?.role === 'development') {
    sectionUrl.searchParams.set('preview_theme_id', window.Shopify.theme.id);
  }
  const controller = new AbortController();
  const requestTimeout = window.setTimeout(() => controller.abort(), 10000);
  let confirmed = false;
  let failure = '';
  try {
    const payload = { items: [{ id: item.variantId, quantity }], sections: sectionIds.slice(0, 5), sections_url: sectionUrl.pathname + sectionUrl.search };
    const response = await fetch(routesRoot + 'cart/add.js', {
      method: 'POST', signal: controller.signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      failure = typeof result.description === 'string' ? result.description : 'Unable to add this item. Please try again.';
      return;
    }
    confirmed = true;
    window.clearTimeout(requestTimeout);
    if (!cartDialog) { window.location.assign(routesRoot + 'cart'); return; }
    const sections = result.sections || {};
    // A null section is a rendering failure, not a failed add. Never retry the add.
    for (const id of sectionIds.slice(0, 5)) {
      if (!sections[id]?.includes(`id="shopify-section-${id}"`)) sections[id] = await sectionRenderer.getSectionHTML(id, false, sectionUrl);
    }
    const html = sections[headerSectionId] || Object.values(sections).find(Boolean);
    const parsed = new DOMParser().parseFromString(html || '', 'text/html');
    const countText = parsed.querySelector('[ref="cartBubbleText"] .visually-hidden')?.textContent;
    let itemCount = Number(countText?.match(/:\s*(\d+)/)?.[1]);
    if (!Number.isFinite(itemCount)) {
      const cartResponse = await fetch(routesRoot + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('Cart count unavailable');
      itemCount = (await cartResponse.json()).item_count;
    }
    document.dispatchEvent(new CartAddEvent(result, 'lngvty-hero-cart', {
      source: 'lngvty-hero-cart', sections, itemCount, variantId: item.variantId, openCart: cartDialog.open && !cartDialog.classList.contains('dialog-closing')
    }));
  } catch (error) {
    failure = confirmed ? 'Your item was added, but the cart could not refresh. Open the cart page to review it.' : 'We could not confirm the cart update. Check your cart before trying again.';
  } finally {
    window.clearTimeout(requestTimeout);
    finishCartUpdate();
    pending = false;
    button.removeAttribute('aria-busy');
    if (failure) showError(failure, button);
    const currentDialog = document.querySelector('.cart-drawer__dialog[open]');
    if (currentDialog) {
      currentDialog.addEventListener('close', () => { setPaused(root, false); restoreFocus(button); }, { once: true });
    } else setPaused(root, false);
  }
}

document.addEventListener('pointerdown', () => { keyboardInteraction = false; }, { capture: true, passive: true });
document.addEventListener('keydown', event => {
  if (event.key === 'Tab' || event.key.startsWith('Arrow') || event.key === 'Enter' || event.key === ' ') {
    keyboardInteraction = true;
    document.querySelectorAll('[data-hero-pointer-focus]').forEach(button => delete button.dataset.heroPointerFocus);
  }
}, { capture: true });

document.addEventListener('touchstart', event => {
  if (!event.target.closest('.lngvty-hero')) return;
  touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
}, { passive: true });
document.addEventListener('touchmove', event => {
  if (touchStart && (Math.abs(event.touches[0].clientX - touchStart.x) > 10 || Math.abs(event.touches[0].clientY - touchStart.y) > 10)) suppressClickUntil = Date.now() + 800;
}, { passive: true });
document.addEventListener('touchend', () => { touchStart = null; }, { passive: true });
document.addEventListener('touchcancel', () => { touchStart = null; }, { passive: true });
document.addEventListener('click', event => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('[data-hero-add-to-cart]');
  if (!button) return;
  event.preventDefault();
  if (button.disabled || pending || (event.detail !== 0 && Date.now() < suppressClickUntil)) return;
  if (event.detail !== 0) button.dataset.heroPointerFocus = 'true';
  else delete button.dataset.heroPointerFocus;
  if (button.dataset.heroPicker) openPicker(button);
  else addProduct(button);
});
document.addEventListener('shopify:section:unload', () => { activePicker?.close(); });

